import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CTASection } from "@/components/site/CTASection";
import { INSTITUTIONAL_PROOFS, WHY_DC_REASONS, ABOUT_PRINCIPLES } from "@/lib/content";
import { Seo } from "@/components/site/Seo";

export default function AboutPage() {
  const { t } = useTranslation();
  const transparentItems = t("common.transparentLimits.items", { returnObjects: true }) || [];
  const breadcrumbs = [
    { name: t("seo.breadcrumbHome"), url: "/" },
    { name: t("seo.about.breadcrumbLabel"), url: "/about" },
  ];
  return (
    <div data-testid="about-page">
      <Seo
        title={t("seo.about.metaTitle")}
        description={t("seo.about.metaDescription")}
        keywords={t("seo.about.keywords")}
        path="/about"
        breadcrumbs={breadcrumbs}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-12 pt-14 sm:px-6 sm:pb-16 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[60px] lg:leading-[1.05]">
            {t("pages.aboutTitle")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">
            {t("pages.aboutSubtitle")}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="about-hero-cta">
              <Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">
                {t("cta.talkToPartner")} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" className="h-11 rounded-lg border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50">
                {t("cta.exploreSolutions")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8" data-testid="principles-section">
        <SectionHeading
          eyebrow={t("sections.aboutPrinc.eyebrow")}
          title={t("sections.aboutPrinc.title")}
          description={t("sections.aboutPrinc.desc")}
          testIdPrefix="principles"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {ABOUT_PRINCIPLES.map((p, idx) => (
            <div key={p.key} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7" data-testid={`principle-card-${idx}`}>
              <div className="text-xs font-bold tracking-[0.2em] text-blue-700">{String(idx + 1).padStart(2, "0")}</div>
              <h3 className="mt-2 text-lg font-extrabold text-slate-900">{t(`content.${p.tk}.title`)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(`content.${p.tk}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50" data-testid="governance-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <SectionHeading
            eyebrow={t("sections.jurisdiction.eyebrow")}
            title={t("sections.jurisdiction.title")}
            description={t("sections.jurisdiction.desc")}
            testIdPrefix="governance"
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {INSTITUTIONAL_PROOFS.map((p) => (
              <div key={p.key} className="rounded-xl border border-slate-200 bg-white p-6">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-50 text-emerald-700">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-bold text-slate-900">{t(`content.${p.tk}.title`)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t(`content.${p.tk}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="why-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <SectionHeading
            eyebrow={t("sections.whyPartner.eyebrow")}
            title={t("sections.whyPartner.title")}
            testIdPrefix="why"
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {WHY_DC_REASONS.map((r, idx) => (
              <div key={r.key} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`about-why-card-${idx}`}>
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-blue-700">
                  <r.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-bold text-slate-900">{t(`content.${r.tk}.title`)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t(`content.${r.tk}.description`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50" data-testid="limitations-section">
        <div className="mx-auto w-full max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-blue-200 bg-white p-6 sm:p-8">
            <h3 className="text-lg font-extrabold text-slate-900">{t("common.transparentLimits.title")}</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              {t("common.transparentLimits.body")}
            </p>
            <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {transparentItems.map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CTASection title={t("ctaSection.aboutTitle")} />
    </div>
  );
}
